import React, { useState, useEffect } from "react";
import { Users, Briefcase, TrendingUp, BarChart3 } from "lucide-react";
import { C, SERIES, pct1, RUTA_POR_CLAVE } from "../igualdad-data.js";
import { apiGet } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { SectionHead, IgualdadDonut, Status } from "../igualdad-components.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Inicio (dashboard)
   ============================================================ */

function IgualdadInicio() {
  const { session } = useIgualdad();
  const go = (v) => window.GO(RUTA_POR_CLAVE[v]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dash, setDash] = useState(null);

  async function load() {
    if (!session.politicaId) { setLoading(false); setDash(null); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await apiGet("/dashboard", { politica_id: session.politicaId });
      setDash(data);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [session.politicaId]);

  const counts = dash?.counts || {};
  const g = dash?.brecha_global || {};
  const personas = counts.personas || 0;
  const puestos = counts.puestos || 0;
  const totM = counts.mujeres || 0;
  const totH = counts.hombres || 0;
  const byCentro = (dash?.by_centro || []).map((r) => ({ name: r.label, value: r.total }));
  const byArea = (dash?.by_area || []).map((r) => ({ name: r.label, value: r.total }));
  const politicas = session.politicas;

  return (
    <div className="rb-page">
      {loading || error ? (
        <Status loading={loading} error={error} />
      ) : (
        <>
          <div className="rb-grid2">
            <div className="rb-card">
              <SectionHead icon={Users} title="Personas" />
              <div className="rb-tilerow">
                <div className="rb-tile">
                  <div className="rb-tile-h">Personas</div>
                  <div className="rb-tile-big">{personas}</div>
                  <div className="rb-tile-trend">{totM} mujeres · {totH} hombres</div>
                </div>
                <div className="rb-tile rb-tile-link" onClick={() => go("personas")}>
                  <div className="rb-tile-h">Análisis salarial</div>
                  <ul className="rb-tile-list"><li>Salarios</li><li>Comparativa</li><li>Consultas</li></ul>
                </div>
                <div className="rb-tile rb-tile-link" onClick={() => go("registro")}>
                  <div className="rb-tile-h">Registro retributivo</div>
                  <div className="rb-tile-big" style={{ color: Math.abs(g.brechaMedia || 0) >= 25 ? C.danger : C.brand }}>{pct1(g.brechaMedia || 0)}</div>
                  <div className="rb-tile-trend">brecha media · ver módulo</div>
                </div>
              </div>
            </div>

            <div className="rb-card">
              <SectionHead icon={Briefcase} title="Puestos" />
              <div className="rb-tilerow">
                <div className="rb-tile rb-tile-link" onClick={() => go("puestos")}>
                  <div className="rb-tile-h">Puestos</div>
                  <div className="rb-tile-big">{puestos}</div>
                  <div className="rb-tile-trend"><TrendingUp size={13} /> valorados por VPT</div>
                </div>
                <div className="rb-tile rb-tile-link" onClick={() => go("estructura")}>
                  <div className="rb-tile-h">Valoración</div>
                  <ul className="rb-tile-list"><li>Valoración</li><li>Estadísticas</li><li>Consultas</li></ul>
                </div>
                <div className="rb-tile">
                  <div className="rb-tile-h">Valorados</div>
                  <IgualdadDonut data={[{ name: "Valorados", value: puestos }, { name: "Sin valoración", value: 0 }]} colors={[C.brand, C.border]} />
                </div>
              </div>
            </div>
          </div>

          <div className="rb-card">
            <SectionHead icon={TrendingUp} title="Políticas retributivas" right={<button className="rb-link" onClick={() => go("estructura")}>Ver todas</button>} />
            <table className="rb-table rb-table-compact">
              <thead><tr><th className="rb-l">Año</th><th className="rb-l">Nombre</th><th>Nº puestos</th><th>Nº personas</th><th className="rb-l">Acciones</th></tr></thead>
              <tbody>
                {politicas.map((p) => (
                  <tr key={p.id}>
                    <td className="rb-l">{p.ejercicio}</td>
                    <td className="rb-l">{p.nombre}</td>
                    <td>{p.id === session.politicaId ? puestos : "—"}</td>
                    <td>{p.id === session.politicaId ? personas : "—"}</td>
                    <td className="rb-l"><span className="rb-pillrow"><span className="rb-pill" onClick={() => go("estructura")}>Gráfico</span><span className="rb-pill" onClick={() => go("estructura")}>Horquilla salarial</span><span className="rb-pill" onClick={() => go("registro")}>Registro</span></span></td>
                  </tr>
                ))}
                {!politicas.length && (<tr><td className="rb-l rb-soft" colSpan={5}>No hay políticas retributivas.</td></tr>)}
              </tbody>
            </table>
          </div>

          <div className="rb-card">
            <SectionHead icon={BarChart3} title="Estadísticas de personas" />
            <div className="rb-grid3">
              <div className="rb-statbox"><div className="rb-statbox-h">Por género</div>
                <IgualdadDonut data={[{ name: "Hombres", value: totH }, { name: "Mujeres", value: totM }]} colors={[C.H, C.M]} /></div>
              <div className="rb-statbox"><div className="rb-statbox-h">Por centro de trabajo</div>
                <IgualdadDonut data={byCentro} colors={SERIES} /></div>
              <div className="rb-statbox"><div className="rb-statbox-h">Por área</div>
                <IgualdadDonut data={byArea} colors={SERIES} /></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { IgualdadInicio };
